import PptxGenJS from 'pptxgenjs';
import {
  AGGREGATE_CAPTION_OPTIONS,
  AGGREGATE_START_X,
  AGGREGATE_STAT_OPTIONS,
  CHART_DEFAULT_STYLE,
  CHART_HEIGHT,
  CHART_START_Y,
  CHART_WIDTH,
  COLOR_SECONDARY,
  COLORS_ACCENT,
  FONT_FAMILY,
  FONT_SIZE_DATA_LABEL,
  FONT_SIZE_P,
  PAGE_START_X,
  PAGE_START_Y,
  QUESTION_PARENT_WIDTH,
  QUESTION_SUBHEADER_HEIGHT,
  QUESTION_SUBHEADER_WIDTH,
  QUESTION_WIDTH,
  SUBHEADER_COLOR,
  SUMMARY_BAR_HEIGHT,
  SUMMARY_BAR_RADIUS,
  SUMMARY_BAR_START_X,
  SUMMARY_BAR_START_Y,
  WHITE_COLOR,
  COLUMN_LABEL_Y_OFFSET,
  BAR_LABEL_X_OFFSET,
  MIN_SEGMENT_SIZE,
  CHAR_WIDTH_6PT,
} from 'common/components/widgets/widget-utils/ppt-export/constants';

import {
  PPTChartData,
  PPTGeographyRecord,
  PPTMultiQuestionSumamryBarRendererParameters,
  PPTRendererParameters,
  PPTSankeyChartResult,
  PPTSummaryBarRendererParameters,
  PPTSurveyQuestionRendererParameters,
  PPTWidgetData,
  PPTWordcloudResult,
} from '../../../../models/pptx.model';
import {
  PPTgetChartCoords,
  PPTmarkdownToText,
  PPTprocessElsieCitationNotes,
  PPTrenderSeparator,
  PPTrenderTable,
} from './pptUtils';
import { LINE_CHART_AXIS_ROTATION, LINE_CHART_COLORS } from '../../../charts/KwelloLineChart';
import { format } from 'date-fns';
import { KwelloBarChartOptions, KwelloLineChartOptions } from '../../../../models/chart.model';
import { castToType, formatAppDate } from '../../../../hooks/utils';
import { ChatResponse } from '../../../../store/api/elsie.api';
import { unified } from 'unified';
import remarkParse from 'remark-parse';
import { SurveyCitation } from '../../../../models/elsie-ai.model';
import { COUNTER_LABELS } from '../../../../constants';
import {
  DEFAULT_BARCHART_OPTIONS,
  DEFAULT_LINECHART_OPTIONS,
} from '../../../../contexts/useWidgetOptionsContext';
import { Aggregate, WidgetType } from 'common/models/widget.model';
import { pushBarChartDisplayLabels } from './patches';

export const PPTsurveyQuestionRenderer = ({
  slide,
  question,
}: PPTSurveyQuestionRendererParameters) => {
  slide.addShape('rect', {
    x: PAGE_START_X,
    y: PAGE_START_Y,
    w: QUESTION_SUBHEADER_WIDTH,
    h: QUESTION_SUBHEADER_HEIGHT,
    fill: { color: SUBHEADER_COLOR },
  });
  slide.addText(
    [
      {
        text: `${question?.parent?.name}`,
        options: {
          fontSize: FONT_SIZE_P + 1,
          color: COLORS_ACCENT,
          fontFace: FONT_FAMILY,
        },
      },
    ],
    {
      x: PAGE_START_X,
      y: PAGE_START_Y,
      w: QUESTION_PARENT_WIDTH,
      h: QUESTION_SUBHEADER_HEIGHT,
      align: 'left',
      wrap: true,
      bold: true,
    }
  );
  slide.addText(
    [
      {
        text: `${question?.name}`,
        options: { fontSize: FONT_SIZE_P, color: COLORS_ACCENT, fontFace: FONT_FAMILY },
      },
    ],
    {
      x: PAGE_START_X + QUESTION_PARENT_WIDTH,
      y: PAGE_START_Y,
      w: QUESTION_WIDTH,
      h: QUESTION_SUBHEADER_HEIGHT,
      align: 'left',
      wrap: true,
    }
  );
};

export const PPTMultiQuestionSummaryRenderer = ({
  slide,
  summaryData,
  startDate,
  endDate,
  position,
}: PPTMultiQuestionSumamryBarRendererParameters) => {
  /* -------------------------------------------------- */
  /* RECT CONFIG                                        */
  /* -------------------------------------------------- */

  const RECT_X = (position?.x ?? SUMMARY_BAR_START_X) - 0.15;
  const RECT_Y = (position?.y ?? SUMMARY_BAR_START_Y) - 0.15;
  const RECT_W = 8.0; // REAL WIDTH
  const RECT_H = SUMMARY_BAR_HEIGHT - 0.07;

  slide.addShape('roundRect', {
    x: RECT_X,
    y: RECT_Y,
    w: RECT_W,
    h: RECT_H,
    fill: { color: WHITE_COLOR },
    line: { color: COLOR_SECONDARY, width: 0.075 },
    rectRadius: SUMMARY_BAR_RADIUS,
  });
  const totalResponses =
    summaryData?.find(({ name }: Aggregate) => name === 'ResponseId')?.value ?? 0;
  const totalHcps = summaryData?.find(({ name }: Aggregate) => name === 'ProfileId')?.value ?? 0;
  const totalResponseDetails =
    summaryData?.find(({ name }: Aggregate) => name === 'ResponseDetail')?.value ?? 0;
  const totalTeamMembers =
    summaryData?.find(({ name }: Aggregate) => name === 'FieldTeamId')?.value ?? 0;
  const formattedStart = format(new Date(startDate), 'dd/MMM/yy');
  const formattedEnd = format(new Date(endDate), 'dd/MMM/yy');

  const ITEMS = [
    { label: 'Total Responses', value: totalResponses, width: 1.3 },
    { label: "Unique HCP's", value: totalHcps, width: 1.3 },
    { label: 'Response Details', value: totalResponseDetails, width: 1.4 },
    { label: 'Team Members', value: totalTeamMembers, width: 1.3 },
    { label: 'Responses from', value: `${formattedStart} - ${formattedEnd}`, width: 2.1 },
  ];

  const SEPARATOR_GAP = 0.12;
  const INNER_PADDING_X = 0.3;
  const CONTENT_WIDTH =
    ITEMS.reduce((sum, i) => sum + i.width, 0) + SEPARATOR_GAP * (ITEMS.length - 1);

  let currentX = RECT_X + INNER_PADDING_X + (RECT_W - INNER_PADDING_X * 2 - CONTENT_WIDTH) / 2;

  const LABEL_Y = RECT_Y + RECT_H * 0.1;
  const VALUE_Y = RECT_Y + RECT_H * 0.38;

  ITEMS.forEach((item, index) => {
    // LABEL
    slide.addText(item.label, {
      x: currentX,
      y: LABEL_Y,
      w: item.width,
      h: 0.25,
      align: 'center',
      color: COLORS_ACCENT,
      fontSize: 8,
    });

    // VALUE
    slide.addText(
      typeof item.value === 'string' ? item.value : Math.round(item.value).toLocaleString('us-US'),
      {
        x: currentX,
        y: VALUE_Y,
        w: item.width,
        h: 0.3,
        align: 'center',
        color: COLORS_ACCENT,
        fontSize: 8,
        bold: true,
      }
    );

    currentX += item.width;

    if (index < ITEMS.length - 1) {
      PPTrenderSeparator(slide, currentX + 0.04, RECT_Y + RECT_H / 2 - 0.05);
      currentX += SEPARATOR_GAP;
    }
  });
};

export const PPTsummaryBarRenderer = ({
  slide,
  summaryData = [],
  isAlignment,
  position,
}: PPTSummaryBarRendererParameters) => {
  const aggregates = summaryData || [];
  const wrapperWidth = 10 - SUMMARY_BAR_START_X * 2;
  const boxWidth = isAlignment ? wrapperWidth / 6 : wrapperWidth / aggregates.length;

  const alignmentTotals = isAlignment ? aggregates.splice(-3) : [];

  const latestAlignment = alignmentTotals.find((agg) => agg.name === 'AverageAlignmentScore');
  const totalUniqueProfiles =
    alignmentTotals.find((agg) => agg.name === 'TotalProfile')?.value ?? 0;
  const totalResponses = alignmentTotals.find((agg) => agg.name === 'TotalResponse')?.value ?? 0;

  slide.addShape('roundRect', {
    x: position?.x ?? SUMMARY_BAR_START_X,
    y: position?.y ?? SUMMARY_BAR_START_Y,
    w: wrapperWidth,
    h: SUMMARY_BAR_HEIGHT,
    fill: { color: WHITE_COLOR },
    line: { color: COLOR_SECONDARY, width: 0.075 },
    rectRadius: SUMMARY_BAR_RADIUS,
  });

  const hasAlignment = latestAlignment;

  aggregates.forEach((stat, index) => {
    const nextBox = index * boxWidth;

    let statValue: string | number = stat.value;
    try {
      statValue = formatAppDate(castToType<string>(statValue));

      // the function returns empty string when !receivedValue
      // and !0 is true...
      if (statValue === '') {
        statValue = 0;
      }
    } catch {
      statValue = Math.round(castToType<number>(statValue)).toLocaleString('us-US');
      // the value is not a date, but that is fine
    }

    slide.addText(
      [
        {
          text: `${statValue}\n`,
          options: AGGREGATE_STAT_OPTIONS,
        },
        {
          text: stat.caption ?? COUNTER_LABELS[stat.name] ?? stat.name,
          options: AGGREGATE_CAPTION_OPTIONS,
        },
      ],
      {
        x: AGGREGATE_START_X + nextBox,
        y: position?.y ?? SUMMARY_BAR_START_Y,
        w: boxWidth,
        h: SUMMARY_BAR_HEIGHT,
        align: 'left',
        valign: 'middle',
        margin: 0.05,
      }
    );
    if (index < aggregates.length - 1 || (index === aggregates.length - 1 && hasAlignment)) {
      const baseX = AGGREGATE_START_X + nextBox;
      const halfBoxWidth = boxWidth / 2;
      PPTrenderSeparator(
        slide,
        baseX + halfBoxWidth + 0.2,
        (position?.y ?? SUMMARY_BAR_START_Y) + 0.3
      );
    }
  });

  if (hasAlignment) {
    slide.addText(
      [
        {
          text: latestAlignment?.value.toString() + '\n',
          options: AGGREGATE_STAT_OPTIONS,
        },
        {
          text: latestAlignment?.caption ?? latestAlignment?.name,
          options: AGGREGATE_CAPTION_OPTIONS,
        },
      ],
      {
        x: AGGREGATE_START_X + aggregates.length * boxWidth,
        y: position?.y ?? SUMMARY_BAR_START_Y,
        w: boxWidth,
        h: SUMMARY_BAR_HEIGHT,
        align: 'left',
        valign: 'middle',
        margin: 0.05,
      }
    );
  }

  if (totalUniqueProfiles && totalResponses) {
    slide.addText(
      [
        {
          text: totalUniqueProfiles.toString(),
          options: AGGREGATE_CAPTION_OPTIONS,
        },
        {
          text: ' Total Unique Profiles\n',
          options: AGGREGATE_CAPTION_OPTIONS,
        },
        {
          text: totalResponses.toString(),
          options: AGGREGATE_CAPTION_OPTIONS,
        },
        {
          text: ' Total Responses',
          options: AGGREGATE_CAPTION_OPTIONS,
        },
      ],
      {
        x: (position?.x ?? SUMMARY_BAR_START_X) + wrapperWidth - boxWidth,
        y: position?.y ?? SUMMARY_BAR_START_Y + 0.05,
        w: boxWidth,
        h: SUMMARY_BAR_HEIGHT,
        align: 'right',
      }
    );
  }
};
function resolveDisplayMode(stackType: string, valueType: string) {
  const isHundredStack = stackType === '100%';
  const isPercentageType = valueType === 'percentage';

  return {
    isHundredStack,
    showPercentageBars: isHundredStack,
    showPercentageLabels: isPercentageType,
  };
}

export const PPTbarChartRenderer = ({
  slide,
  data,
  chartOptions,
  overrideRenderOptions,
  widget,
  position,
}: PPTRendererParameters<PPTChartData[]>): void => {
  const chartOptionsWithDefaults: KwelloBarChartOptions = {
    ...DEFAULT_BARCHART_OPTIONS,
    ...widget?.barChartOptions,
    ...chartOptions?.barChartOptions,
  };

  const stackType = chartOptionsWithDefaults.stack ?? 'normal';
  const valueType = chartOptionsWithDefaults.valueType ?? 'value';

  const stackTypeStr = Array.isArray(stackType) ? stackType[0] : stackType;
  const valueTypeStr = Array.isArray(valueType) ? valueType[0] : valueType;

  const { isHundredStack, showPercentageBars, showPercentageLabels } = resolveDisplayMode(
    stackTypeStr,
    valueTypeStr
  );

  const isSeriesArray = (d: unknown): d is PPTChartData[] =>
    Array.isArray(d) && d.length > 0 && Array.isArray(d[0].values);

  let maxTotal = 0;

  if (isSeriesArray(data)) {
    if (isHundredStack) {
      maxTotal = 1;
    } else {
      maxTotal = Math.max(
        ...data[0].values.map((_, i) =>
          data.reduce((sum, s) => sum + (Number(s.values[i]) || 0), 0)
        )
      );
    }
  }

  const valAxisMinVal = 0;
  let valAxisMaxVal = 1;
  let valAxisLabelFormatCode = '0';
  let valAxisMajorUnit: number | undefined;

  if (isHundredStack) {
    valAxisMaxVal = 1;
    valAxisLabelFormatCode = '0%';
    valAxisMajorUnit = 0.2;
  } else if (valueTypeStr === 'percentage') {
    const base = Math.ceil(maxTotal / 5) * 5;
    valAxisMaxVal = base + base * 0.1; // 10% buffer, matching UI (KwelloBarChart)
    valAxisLabelFormatCode = '0';
    // No explicit majorUnit — let PowerPoint auto-calculate (matches Kendo UI behavior)
  } else {
    valAxisMaxVal = Math.ceil(maxTotal / 5) * 5;
    valAxisLabelFormatCode = '0';
    valAxisMajorUnit = maxTotal < 10 ? 1 : undefined;
  }

  const getStack = () => {
    if (stackTypeStr === 'normal') return 'stacked';
    if (stackTypeStr === '100%') return 'percentStacked';
    return undefined;
  };

  const getDirection = () => (chartOptionsWithDefaults.direction === 'horizontal' ? 'bar' : 'col');
  const colors = castToType<string[]>(data.map((d) => d.color).filter((c) => c !== undefined));

  const seriesDisplayType = Array.isArray(chartOptionsWithDefaults.seriesValueDisplayType)
    ? chartOptionsWithDefaults.seriesValueDisplayType[0]
    : chartOptionsWithDefaults.seriesValueDisplayType;
  const hideLabels = seriesDisplayType === 'none';

  const renderOpts: PptxGenJS.IChartOpts = {
    ...CHART_DEFAULT_STYLE(colors, data.length),
    barDir: getDirection(),
    barGrouping: getStack(),
    showPercent: showPercentageBars,
    showLegend: true,
    legendPos: 'b',
    showValue: !hideLabels,
    showLabel: !hideLabels,
    dataLabelFormatCode:
      (showPercentageLabels || seriesDisplayType === 'total') && !hideLabels
        ? '__SHOW_PERCENT__'
        : '0',
    dataLabelFontSize: FONT_SIZE_DATA_LABEL,
    dataLabelColor:
      widget?.type === WidgetType.ALIGNMENT_DISTRIBUTION ? COLORS_ACCENT : WHITE_COLOR,
    catAxisLabelFontFace: FONT_FAMILY,
    legendFontFace: FONT_FAMILY,
    legendFontSize: FONT_SIZE_P,
  };

  // Register per-point display labels for PPTX XML post-processing when:
  // - percentage mode (segment labels show displayValue %, not value %)
  // - total mode (single summed label per category, any valueType)
  const useCustomLabels = (showPercentageLabels || seriesDisplayType === 'total') && !hideLabels;
  if (useCustomLabels) {
    // Total labels appear outside the bar (manual offset), so always use a visible color.
    // Segment labels appear inside colored bars, so use white (except for Alignment Distribution).
    const rawColor =
      seriesDisplayType === 'total' || widget?.type === WidgetType.ALIGNMENT_DISTRIBUTION
        ? COLORS_ACCENT
        : WHITE_COLOR;
    const hex = rawColor.replace('#', '');
    const fullHex = hex.length === 3 ? hex.replace(/./g, '$&$&') : hex;

    let seriesLabels: string[][];
    let labelPosition: string | undefined;
    let layoutOffset: { x: string; y: string } | undefined;

    if (seriesDisplayType === 'total') {
      // Total mode: show one summed label per category, placed on the last series
      // that has a non-NaN value for that point (so PowerPoint can anchor the label).
      const numPoints = data[0]?.values?.length ?? 0;
      seriesLabels = data.map(() => new Array<string>(numPoints).fill(''));

      for (let i = 0; i < numPoints; i++) {
        const sum = data.reduce((acc, s) => acc + (Number(s.values[i]) || 0), 0);
        if (sum === 0) continue;

        // Find last series with a real bar for this point
        let target = data.length - 1;
        for (let s = data.length - 1; s >= 0; s--) {
          if (Number.isFinite(data[s].values[i])) {
            target = s;
            break;
          }
        }
        seriesLabels[target][i] = showPercentageLabels
          ? `${parseFloat(sum.toFixed(1))}%`
          : `${Math.round(sum)}`;
      }
      // 'outEnd' is invalid for stacked bars in OOXML; 'inEnd' places the label
      // at the edge of the last segment. Manual offset nudges it just outside.
      labelPosition = 'inEnd';
      const isColumn = chartOptionsWithDefaults.direction === 'vertical';
      layoutOffset = isColumn
        ? { x: '0', y: COLUMN_LABEL_Y_OFFSET }
        : { x: BAR_LABEL_X_OFFSET, y: '0' };
    } else {
      // Segment mode: per-segment labels from displayLabels.
      // Hide labels for tiny segments (matching the UI's legibility behavior in
      // KwelloBarChart.tsx seriesLabelVisual). The UI checks pixel dimensions;
      // here we estimate the segment's absolute size on the slide by comparing
      // its value to the axis maximum and the chart's physical dimensions.
      //
      // For vertical columns: segment height = (value / axisMax) × chartHeight.
      //   Hide if shorter than ~0.15 in (a 6pt label needs ~0.1 in + padding).
      // For horizontal bars: segment width = (value / axisMax) × chartWidth.
      //   Hide if narrower than the label text (~charCount × 0.045 in at 6pt).
      const isColumn = chartOptionsWithDefaults.direction === 'vertical';
      const axisSize = isColumn ? CHART_HEIGHT : CHART_WIDTH;

      seriesLabels = data.map((s, sIdx) => {
        const labels = s.displayLabels ?? [];
        return labels.map((label, pIdx) => {
          if (!label) return label;
          const segmentValue = Number(data[sIdx].values[pIdx]) || 0;
          if (segmentValue === 0) return '';
          const segmentSize = (segmentValue / valAxisMaxVal) * axisSize;

          if (isColumn) {
            return segmentSize < MIN_SEGMENT_SIZE ? '' : label;
          } else {
            const labelWidth = label.length * CHAR_WIDTH_6PT;
            return segmentSize < labelWidth ? '' : label;
          }
        });
      });
    }

    pushBarChartDisplayLabels({
      seriesLabels,
      fontSize: Math.round((FONT_SIZE_DATA_LABEL || 9) * 100),
      fontColor: fullHex.toUpperCase(),
      fontFace: FONT_FAMILY || 'Arial',
      labelPosition,
      layoutOffset,
    });
  }

  slide.addChart('bar', data, {
    ...renderOpts,
    catAxisLabelFontSize: FONT_SIZE_P,
    ...PPTgetChartCoords(position),
    ...overrideRenderOptions,
    valAxisMinVal,
    valAxisMaxVal,
    valAxisLabelFormatCode,
    valAxisMajorUnit,
  });
};

export const PPTsankeyChartRenderer = ({
  slide,
  data,
  overrideRenderOptions,
}: PPTRendererParameters<PPTSankeyChartResult>): void => {
  const { image, latestAlignment, previousAlignment } = data;
  if (Object.keys(latestAlignment).length > 0) {
    PPTrenderTable(
      previousAlignment,
      slide,
      'Previous Alignment',
      '#29B2FF',
      SUMMARY_BAR_START_X,
      CHART_START_Y
    );
  }

  if (Object.keys(previousAlignment).length > 0) {
    PPTrenderTable(
      latestAlignment,
      slide,
      'Latest Alignment',
      '#03386F',
      SUMMARY_BAR_START_X + 7.65,
      CHART_START_Y
    );
  }
  slide.addImage({
    data: `data:image/svg+xml;base64,${image}`,
    x: PAGE_START_X,
    y: CHART_START_Y + CHART_HEIGHT / 2 - 1.5,
    sizing: { type: 'contain', w: CHART_WIDTH, h: CHART_HEIGHT },
    ...overrideRenderOptions,
  });
};

export const PPTwordcloudRenderer = ({
  slide,
  data,
  overrideRenderOptions,
  position,
}: PPTRendererParameters<PPTWordcloudResult>): void => {
  slide.addImage({
    data: `data:image/svg+xml;base64,${data.image}`,
    ...PPTgetChartCoords(position),
    sizing: { type: 'contain', w: CHART_WIDTH, h: CHART_HEIGHT },
    ...overrideRenderOptions,
  });
};

export const PPTdoughnutChartRenderer = ({
  slide,
  data,
  overrideRenderOptions,
  position,
}: PPTRendererParameters<PPTWidgetData[]>): void => {
  const renderOpts: PptxGenJS.IChartOpts = {
    ...CHART_DEFAULT_STYLE(),
    showLegend: true,
    legendPos: 'b',
    showLabel: false,
    showPercent: true,
    holeSize: 65,
    catAxisLabelFontFace: FONT_FAMILY,
    catAxisLabelFontSize: FONT_SIZE_P,
    catAxisLabelFontBold: false,
    legendFontFace: FONT_FAMILY,
    legendFontSize: FONT_SIZE_P,
    catAxisLabelPos: 'high',
    dataLabelFormatCode: '0.0%',
    dataLabelFontSize: FONT_SIZE_DATA_LABEL,
  };
  slide.addChart('doughnut', data, {
    ...renderOpts,
    ...PPTgetChartCoords(position),
    ...overrideRenderOptions,
  });
};

export const PPTlineChartRenderer = ({
  slide,
  data,
  chartOptions,
  widget,
  overrideRenderOptions,
  position,
}: PPTRendererParameters<PPTWidgetData[]>): void => {
  const chartOptionsWithDefaults: KwelloLineChartOptions = {
    ...DEFAULT_LINECHART_OPTIONS,
    ...widget?.lineChartOptions,
    ...chartOptions?.lineChartOptions,
  };

  const shouldSmoothLine = chartOptionsWithDefaults?.lineStyle === 'smooth';

  function extractValues(series: PPTWidgetData): number[] {
    if (
      series &&
      typeof series === 'object' &&
      'values' in series &&
      Array.isArray(series.values)
    ) {
      return series.values.map((v) => (typeof v === 'number' ? v : Number(v) || 0));
    }
    return [];
  }

  function extractAllValues(seriesArr: PPTWidgetData[]): number[] {
    return seriesArr.flatMap((s) => extractValues(s));
  }

  const allValues = extractAllValues(data).filter((value) => Number.isFinite(value));
  const rawMin = allValues.length > 0 ? Math.min(...allValues) : 0;
  const rawMax = allValues.length > 0 ? Math.max(...allValues) : 0;

  const minMaxGap = chartOptionsWithDefaults.minMaxGap ?? 0;
  const configuredMajorUnit = chartOptionsWithDefaults.yMajorUnit;

  const decimalPlacesFromMajorUnit = configuredMajorUnit
    ? configuredMajorUnit.toString().split('.')[1]?.length ?? 0
    : 0;

  const flooredMin = decimalPlacesFromMajorUnit
    ? parseFloat(rawMin.toFixed(decimalPlacesFromMajorUnit))
    : rawMin;
  const flooredMax = decimalPlacesFromMajorUnit
    ? parseFloat(rawMax.toFixed(decimalPlacesFromMajorUnit))
    : rawMax;

  let valAxisMinVal: number | undefined;
  let valAxisMaxVal: number | undefined;
  const valAxisMajorUnit = configuredMajorUnit ?? undefined;

  if (minMaxGap) {
    valAxisMinVal = flooredMin - minMaxGap;
    valAxisMaxVal = flooredMax + minMaxGap;

    if (valAxisMaxVal === valAxisMinVal) {
      valAxisMaxVal += valAxisMajorUnit ?? 1;
    }
  }

  const valAxisLabelDecimals = valAxisMajorUnit
    ? valAxisMajorUnit.toString().split('.')[1]?.length ?? 0
    : 0;
  const valAxisLabelFormatCode = valAxisLabelDecimals
    ? `0.${'0'.repeat(valAxisLabelDecimals)}`
    : '0';

  const renderOpts: PptxGenJS.IChartOpts = {
    ...CHART_DEFAULT_STYLE(LINE_CHART_COLORS, data.length),
    showLegend: true,
    legendPos: 'b',
    showLabel: true,
    lineSmooth: shouldSmoothLine,
    catAxisLabelRotate: LINE_CHART_AXIS_ROTATION,
    barGrouping: 'standard',
    dataLabelFontSize: FONT_SIZE_DATA_LABEL,
    dataLabelColor: COLORS_ACCENT,
    chartArea: { roundedCorners: shouldSmoothLine },
    dataLabelPosition: 'bestFit',
  };
  // TOODO always be line chart for exporting. no area charts
  slide.addChart('line', data, {
    ...renderOpts,
    ...PPTgetChartCoords(position),
    ...overrideRenderOptions,
    ...(valAxisMinVal !== undefined ? { valAxisMinVal } : {}),
    ...(valAxisMaxVal !== undefined ? { valAxisMaxVal } : {}),
    valAxisLabelFormatCode,
    ...(valAxisMajorUnit !== undefined ? { valAxisMajorUnit } : {}),
  });
};

export const PPTgeographyChartRenderer = ({
  slide,
  data,
}: PPTRendererParameters<PPTGeographyRecord[]>): void => {
  const tableData: PptxGenJS.TableRow[] = [
    ...data.map(({ country, value }) => [
      { text: country, options: { fontSize: 12 } },
      { text: value.toString(), options: { fontSize: 12 } },
    ]),
  ];
  slide.addTable(tableData, {
    objectName: 'geographyTable',
  });
};

export const PPTelsieSummaryRenderer = ({
  slide,
  data,
}: PPTRendererParameters<{ summary: ChatResponse; citationsData: SurveyCitation[] }>) => {
  const processor = unified().use(remarkParse);
  const tree = processor.parse(data.summary?.chatResponse);
  const notes = PPTprocessElsieCitationNotes(data.summary?.citationOrdinals, data.citationsData);
  const textSegments: PptxGenJS.TextProps[] = [];
  tree.children.forEach((node) => {
    if (node.type === 'heading') {
      return;
    }
    if (node.type === 'paragraph') {
      const para = node;
      const result = PPTmarkdownToText(para);
      textSegments.push(...result);
      textSegments.push({ text: '\n', options: { fontSize: FONT_SIZE_P } }); // New line after each paragraph
    }
    if (node.type === 'list') {
      textSegments.push({ text: '\n', options: { fontSize: FONT_SIZE_P } }); // New line before the list
      node.children.forEach((item) => {
        const bullet = '•';
        const content = PPTmarkdownToText(item.children[0]);
        textSegments.push({
          text: bullet,
          options: { fontSize: FONT_SIZE_P, fontFace: FONT_FAMILY, color: COLORS_ACCENT },
        });
        textSegments.push(...content);
        textSegments.push({ text: '\n\n', options: { fontSize: FONT_SIZE_P } }); // New line after each list item
      });
      return; // skip the outer list node itself
    }
  });
  // Add text to slide
  if (textSegments.length > 0) {
    slide.addText(textSegments, {
      x: 0,
      y: SUMMARY_BAR_START_Y,
      w: '100%',
      h: 0.5,
      valign: 'top',
    });
  }

  slide.addImage({
    path: '/src/assets/avatars/elsieai-avatar.svg',
    x: 0.25,
    y: '80%',
    w: 0.5,
    h: 0.5,
  });
  slide.addNotes(notes);
};
